import React, { useMemo, useState } from "react";
import BottomSheet from "../../goals/BottomSheet";
import { useUserEvents } from "../../../hooks/useUserEvents";
import { eventLastDay, eventTypeLabel } from "../../../types/event";
import { linkPerformanceToEvent } from "../../../services/performanceService";
import { formatDateRange } from "../../../utils/goalDates";

interface LinkToEventDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  trainingLogId: string;
  /** Date of the performance (YYYYMMDD) — events nearest to it are listed first. */
  dateID: string;
  onLinked?: (eventId: string) => void;
}

/**
 * "Link to event" picker for a performance (Events.md §3.4). Lists the
 * athlete's events nearest the performance date first, then calls
 * `linkPerformanceToEvent`.
 */
const LinkToEventDialog: React.FC<LinkToEventDialogProps> = ({
  isOpen,
  onClose,
  userId,
  trainingLogId,
  dateID,
  onLinked,
}) => {
  const { events, loading } = useUserEvents(isOpen ? userId : undefined);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sorted = useMemo(() => {
    const distance = (date: string, last: string) =>
      dateID >= date && dateID <= last
        ? 0
        : Math.min(
            Math.abs(Number(date) - Number(dateID)),
            Math.abs(Number(last) - Number(dateID)),
          );
    return events
      .filter((e) => e.status !== "withdrawn")
      .sort(
        (a, b) =>
          distance(a.date, eventLastDay(a)) - distance(b.date, eventLastDay(b)),
      );
  }, [events, dateID]);

  const handlePick = async (eventId: string) => {
    setBusyId(eventId);
    setError(null);
    try {
      await linkPerformanceToEvent(userId, trainingLogId, eventId);
      onLinked?.(eventId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not link performance");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Link to event"
      subtitle="Pick the event this performance belongs to."
      busy={busyId !== null}
    >
      {loading ? (
        <div className="text-center py-4">
          <span className="loading loading-spinner loading-sm" />
        </div>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-base-content/60 font-grotesk py-2">
          No events yet. Add one from the Schedule.
        </p>
      ) : (
        <div className="space-y-2">
          {sorted.map((e) => (
            <button
              key={e.id}
              type="button"
              disabled={busyId !== null}
              onClick={() => handlePick(e.id)}
              className="w-full text-left rounded-xl border border-base-300 bg-base-100 p-3 hover:border-base-content/40 transition-colors disabled:opacity-50"
            >
              <span className="block text-sm font-medium truncate">{e.name}</span>
              <span className="block text-xs text-base-content/60 font-grotesk">
                {eventTypeLabel(e.type)} · {formatDateRange(e.date, e.endDate)}
              </span>
            </button>
          ))}
        </div>
      )}
      {error && <p className="text-sm text-error font-grotesk">{error}</p>}
    </BottomSheet>
  );
};

export default LinkToEventDialog;
